import * as Popover from "@radix-ui/react-popover";
import { Editor } from "@tiptap/core";
import classNames from "classnames";
import { useState, useEffect, useCallback, useRef } from "react";
import SVG from "react-inlinesvg";
import { cn } from "src/lib/cn";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";
import { sendAppEvent } from "src/Utils";
import { keepFocusInEditorIfEventFromEditor } from "src/components/TiptapDocumentSB/helpers";
import { HIGHLIGHT_COLORS, HighlightColorDefinition } from "../../../../../../src/shared/highlightColors";

// Re-export for backwards compatibility
export { HIGHLIGHT_COLORS };

// ID for the injected selection color style in the main document
const SELECTION_STYLE_ID = "highlight-selection-override";

/**
 * Hook to detect if we're in dark mode based on body[data-theme-type]
 */
function useIsDarkMode(): boolean {
  const [isDark, setIsDark] = useState(() => {
    return document.body.getAttribute("data-theme-type") === "dark";
  });

  useEffect(() => {
    // Observe changes to the body's data-theme-type attribute
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "data-theme-type") {
          setIsDark(document.body.getAttribute("data-theme-type") === "dark");
        }
      }
    });

    observer.observe(document.body, { attributes: true });

    return () => observer.disconnect();
  }, []);

  return isDark;
}

/**
 * Get the display color based on current theme
 */
function getDisplayColor(lightColor: string, isDark: boolean): string {
  const colorPair = HIGHLIGHT_COLORS.find((c: HighlightColorDefinition) => c.light === lightColor);
  if (colorPair) {
    return isDark ? colorPair.dark : colorPair.light;
  }
  return lightColor;
}

interface HighlightColorPickerProps {
  editor: Editor | null;
  className?: string;
}

export function HighlightColorPicker({ editor, className }: HighlightColorPickerProps) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  // Save selection when popover opens so we can restore it after applying highlight
  const [savedSelection, setSavedSelection] = useState<{ from: number; to: number } | null>(null);
  const isDarkMode = useIsDarkMode();

  const isHighlightActive = editor?.isActive("highlight");
  const currentColor = editor?.getAttributes("highlight")?.color;

  /**
   * Override the editor's selection color to match the highlight color.
   * This makes the highlight visible even while text is selected.
   */
  const setSelectionColor = useCallback((color: string | null) => {
    // Remove existing override
    const existingStyle = document.getElementById(SELECTION_STYLE_ID);
    if (existingStyle) {
      existingStyle.remove();
    }

    // If a color is provided, inject new style (scoped to the manuscript
    // editor so other tiptap instances keep the default selection color)
    if (color) {
      const displayColor = getDisplayColor(color, isDarkMode);
      const style = document.createElement("style");
      style.id = SELECTION_STYLE_ID;
      style.textContent = `
        #editorContainer .ProseMirror ::selection {
          background-color: ${displayColor} !important;
        }
        #editorContainer .ProseMirror ::-moz-selection {
          background-color: ${displayColor} !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, [isDarkMode]);

  // Clean up selection color override when selection changes in the editor
  useEffect(() => {
    if (!editor) return;

    const handleSelectionUpdate = () => {
      // If no text is selected (cursor only), remove the selection color override
      const { from, to } = editor.state.selection;
      if (from === to) {
        setSelectionColor(null);
      }
    };

    editor.on("selectionUpdate", handleSelectionUpdate);
    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate);
    };
  }, [editor, setSelectionColor]);

  // The override lives in the main document now (the iframe document used to
  // be discarded with the editor), so remove it when the picker unmounts.
  useEffect(() => {
    return () => {
      document.getElementById(SELECTION_STYLE_ID)?.remove();
    };
  }, []);

  // Track when we're programmatically focusing the editor (to ignore the resulting iframe events)
  const isApplyingColorRef = useRef(false);

  // Close popover when clicking outside (both main page and iframe)
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Don't close if clicking inside the popover content or trigger (identified by data attribute)
      if (target.closest('[data-highlight-picker]')) {
        return;
      }

      // Ignore editor clicks that result from our programmatic focus
      const container = document.getElementById("editorContainer");
      if (isApplyingColorRef.current && container && target instanceof Node && container.contains(target)) {
        return;
      }

      setOpen(false);
    };

    // Editor clicks bubble to the main document now, so one listener covers
    // both the app chrome and the editor.
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handlePresetColorClick = (color: string) => {
    if (editor && savedSelection) {
      // Set selection color to match highlight so it's immediately visible
      setSelectionColor(color);

      // Mark that we're applying a color (to prevent iframe click handler from closing)
      isApplyingColorRef.current = true;

      // Restore selection and apply highlight
      editor
        .chain()
        .focus()
        .setTextSelection(savedSelection)
        .setHighlight({ color })
        .run();

      // Track the highlight event
      const colorName = HIGHLIGHT_COLORS.find((c: HighlightColorDefinition) => c.light === color)?.name || "unknown";
      sendAppEvent("Applied Text Highlight", { color: colorName });

      // Dynamic import avoids pulling UserData/firebase into every bundle consumer at module load (breaks tests/CI).
      void (async () => {
        try {
          const { useSudowriteStore } = await import("src/stores/sudowriteStore");
          await useSudowriteStore.getState().userData?.update({ preferredManuscriptHighlightLight: color });
        } catch {
          /* non-fatal */
        }
      })();

      // Reset the flag after a short delay
      setTimeout(() => {
        isApplyingColorRef.current = false;
      }, 100);
    }
    // Don't close - let user try different colors. Closes on click outside.
  };

  const handleRemoveHighlight = () => {
    if (editor && savedSelection) {
      // Remove selection color override
      setSelectionColor(null);

      // Mark that we're applying a color (to prevent iframe click handler from closing)
      isApplyingColorRef.current = true;

      // Restore selection and remove highlight
      editor
        .chain()
        .focus()
        .setTextSelection(savedSelection)
        .unsetHighlight()
        .run();

      // Reset the flag after a short delay
      setTimeout(() => {
        isApplyingColorRef.current = false;
      }, 100);
    }
    // Don't close - let user change their mind. Closes on click outside.
  };

  return (
    <SudoTooltip
      text="Highlight"
      disabled={!hovered || open}
      hasArrow={false}
      triggerClassName="max-w-[80px]"
      position="bottom"
    >
      {/* Non-modal: a modal popover's focus trap would fight the
          editor.chain().focus() we run after applying a color. In the iframe
          era the trap couldn't cross documents (and the iframe painted its
          selection regardless of focus); now the editor must genuinely hold
          focus so the native selection — recolored by the ::selection
          override — paints over the fresh highlight, and so the empty
          selectionUpdate that clears the override can fire later. Outside
          clicks already close via our document mousedown listener. */}
      <Popover.Root open={open} modal={false}>
        <Popover.Trigger asChild>
          <div
            data-highlight-picker
            role="button"
            tabIndex={0}
            className={classNames(
              "icon-button-wrapper toolbar-icon flex items-center justify-center w-10 h-10 cursor-pointer",
              { active: isHighlightActive },
              className
            )}
            aria-label="Text Highlight Color"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => {
              if (!open && editor) {
                const { from, to } = editor.state.selection;
                setSavedSelection({ from, to });
              }
              setOpen(!open);
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                if (!open && editor) {
                  const { from, to } = editor.state.selection;
                  setSavedSelection({ from, to });
                }
                setOpen(!open);
              }
            }}
          >
            <div className="icon-wrapper flex items-center justify-center w-8 h-8 transition-all duration-200 rounded-lg hover:bg-[var(--menu-color-hover)]">
              <SVG
                src="/assets/editor-toolbar/highlight.svg"
                className="w-6 h-6"
                style={{
                  // Hide the color indicator bar when no highlight is selected
                  color: currentColor ? getDisplayColor(currentColor, isDarkMode) : "transparent",
                }}
              />
            </div>
          </div>
        </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          data-highlight-picker
          side="bottom"
          align="center"
          sideOffset={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={keepFocusInEditorIfEventFromEditor}
          className={cn(
            "z-[100] origin-top animate-flyout shadow-card overflow-hidden",
            "rounded-xl",
            "bg-[var(--menu-color)] p-3"
          )}
          style={{ border: "1px solid var(--grey-2)" }}
        >
          {/* Remove highlight button - at top like Google Docs */}
          <div className="flex justify-center pb-3 border-b border-app-divider">
            <SudoButton
              variant="tertiaryGrey"
              size="xsmall"
              onClick={handleRemoveHighlight}
              aria-label="Remove highlight"
            >
              None
            </SudoButton>
          </div>

          {/* Preset Colors Grid - 6 colors in a row */}
          <div
            className="flex gap-2"
            role="listbox"
            aria-label="Highlight colors"
          >
            {HIGHLIGHT_COLORS.map((colorPair: HighlightColorDefinition) => {
              const displayColor = isDarkMode ? colorPair.dark : colorPair.light;
              const isSelected = currentColor === colorPair.light;

              return (
                <button
                  key={colorPair.light}
                  type="button"
                  onClick={() => handlePresetColorClick(colorPair.light)}
                  className={cn(
                    "w-7 h-7 rounded-full border-none hover:scale-110 transition-transform cursor-pointer",
                    "focus:outline-none focus:ring-2 focus:ring-offset-1",
                    isSelected && "ring-2 ring-offset-1"
                  )}
                  style={{
                    backgroundColor: displayColor,
                    "--tw-ring-color": "var(--primary-cta)",
                  } as React.CSSProperties}
                  aria-label={`Highlight color ${colorPair.name}`}
                  aria-selected={isSelected}
                />
              );
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
    </SudoTooltip>
  );
}
